import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../services/authFetch';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// useSubscription no longer needed — see Dashboard component for context.
// CoachCard hidden for launch — re-enable once ESLC topic frequency logic is built
// import CoachCard, { CoachAction } from '../components/CoachCard/CoachCard';
import StudyReport from '../components/StudyReport/StudyReport';
import QuickStart from '../components/QuickStart/QuickStart';
import PredictionSurveyModal from '../components/PredictionSurveyModal';
import ShareLomi from '../components/ShareLomi/ShareLomi';
import { FeedbackAutoModal, HelpShapeLomiItem } from '../components/feedback/FeedbackModal';
import PackageSwitcher from '../components/Package/PackageSwitcher';
import EauPrompt from '../components/EauPrompt';
import UatPrompt from '../components/UatPrompt';
import { diagnosticAPI, studentAPI, track } from '../services/api';
import Logo from '../components/Logo';
import ThemeMenuToggle from '../components/ThemeMenuToggle';
import { useTheme } from '../contexts/ThemeContext';
import InboxBell from '../components/InboxBell';
import AptitudeDrillCard from '../components/AptitudeDrillCard';
import DailyFocusCard from '../components/DailyFocusCard';
import MasterYourMisses from '../components/MasterYourMisses';
import StoryInviteCard from '../components/StoryInviteCard';
import TestimonialsPromo from '../components/TestimonialsPromo';
import Avatar from '../components/Avatar';
import './Dashboard.css';

const QUICK_START_KEY = 'lomi_quick_start_completed';

const Dashboard: React.FC = () => {
  const { student, logout, refreshProfile } = useAuth();
  const { theme, preference } = useTheme();
  const isDark = theme === 'dark';

  // Ambient dark-mode adoption: emit the resolved theme once per browser
  // session when a student first lands on the dashboard, so we can measure the
  // share of sessions in dark (the toggle event only fires on an explicit
  // switch). localStorage still holds the preference — this is telemetry only.
  useEffect(() => {
    try {
      if (!sessionStorage.getItem('lomi_theme_logged')) {
        track('theme.session', { theme, preference });
        sessionStorage.setItem('lomi_theme_logged', '1');
      }
    } catch {
      /* sessionStorage unavailable — skip */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Stream set/change banner — for the ~9% with no stream (served untargeted
  // questions from the union of all subjects). One tap sets it + refreshes.
  const [settingStream, setSettingStream] = useState(false);
  const setStream = async (stream: 'natural' | 'social') => {
    if (settingStream) return;
    setSettingStream(true);
    try {
      track('stream.set_from_banner', { stream });
      await studentAPI.updateProfile({ stream });
      await refreshProfile();
    } catch {
      setSettingStream(false);
    }
  };
  // useSubscription's hasSubscription is no longer read — every student
  // shows the Trial badge while the free-trial launch window is on.
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [storyInviteDismissed, setStoryInviteDismissed] = useState(
    () => localStorage.getItem('lomi_story_invite_dismissed') === '1',
  );
  const accountMenuRef = useRef<HTMLDivElement>(null);

  // Study Report modal
  const [showStudyReport, setShowStudyReport] = useState(false);

  // ESSLCE prediction survey — auto-opens 2.5s after Dashboard mount when
  // /prediction/state.should_prompt = true (≥5 questions answered, no
  // prediction yet, skip cooldown elapsed). Moved here from
  // AssessmentResults 2026-05-09: only ~13% of students complete a quiz,
  // but Dashboard is universal.
  const [showSurvey, setShowSurvey] = useState(false);

  // (KC nudge banner removed from this page — state no longer needed.)

  // First-visit tutorial. QuickStart lives on the dashboard (not gated into
  // the KC flow) so students can orient themselves without being funneled
  // straight into a test.
  const [showQuickStart, setShowQuickStart] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(QUICK_START_KEY) !== 'true'
  );
  const finishQuickStart = () => {
    try {
      localStorage.setItem(QUICK_START_KEY, 'true');
    } catch {}
    setShowQuickStart(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setShowAccountMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch all-time rank once per page load. Silent on failure.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch('/api/gamification/my-rank');
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setRankData({
          rank: data.rank?.rank ?? null,
          xp: data.rank?.xp ?? null,
          total: data.rank?.total_ranked ?? null,
        });
      } catch {
        // Non-critical
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const [availableSubjects, setAvailableSubjects] = useState<Array<{name: string, icon: string, color: string}>>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [subjectStats, setSubjectStats] = useState<Record<string, {
    topic_count: number;
    module_count: number;
    completion_percentage: number;
    in_progress_count: number;
    completed_count: number;
    time_remaining_formatted: string;
  }>>({});
  const [streakData, setStreakData] = useState<{
    streak_days: number;
    last_7_days: boolean[];
    total_sessions_30d: number;
  }>({ streak_days: 0, last_7_days: [false, false, false, false, false, false, false], total_sessions_30d: 0 });
  const [rankData, setRankData] = useState<{
    rank: number | null;
    xp: number | null;
    total: number | null;
  }>({ rank: null, xp: null, total: null });
  const [diagnosticStatus, setDiagnosticStatus] = useState<Record<string, {
    available: boolean;
    assessment_id: string;
    question_count: number;
    taken: boolean;
    score: number | null;
    can_retake?: boolean;
    cooldown_days_left?: number;
  }>>({});

  // Fetch subjects from API based on student stream
  useEffect(() => {
    const fetchSubjects = async () => {
      if (!student?.stream) {
        setLoadingSubjects(false);
        return;
      }

      try {
        setLoadingSubjects(true);
        const response = await authFetch(`/api/subjects/${student.stream}`);

        if (!response.ok) {
          throw new Error('Failed to fetch subjects');
        }

        const data = await response.json();

        // Map API response to component format
        const subjects = data.subjects.map((subject: any) => ({
          name: subject.name,
          icon: subject.icon || '📚',
          color: subject.color || '#4CAF50'
        }));

        setAvailableSubjects(subjects);
      } catch (error) {
        console.error('Error fetching subjects:', error);

        // Fallback to hardcoded subjects if API fails
        if (student?.stream === 'natural') {
          setAvailableSubjects([
            { name: 'Biology', icon: '🧬', color: '#4CAF50' },
            { name: 'Chemistry', icon: '⚗️', color: '#2196F3' },
            { name: 'Physics', icon: '⚛️', color: '#FF9800' },
            { name: 'Mathematics', icon: '📐', color: '#9C27B0' },
            { name: 'English', icon: '📚', color: '#3F51B5' }
          ]);
        } else if (student?.stream === 'social') {
          setAvailableSubjects([
            { name: 'History', icon: '📜', color: '#795548' },
            { name: 'Geography', icon: '🌍', color: '#009688' },
            { name: 'Economics', icon: '💰', color: '#FF5722' },
            { name: 'Mathematics', icon: '📐', color: '#9C27B0' },
            { name: 'English', icon: '📚', color: '#3F51B5' }
          ]);
        }
      } finally {
        setLoadingSubjects(false);
      }
    };

    fetchSubjects();
  }, [student?.stream]);

  // Fetch subject stats after subjects are loaded
  useEffect(() => {
    const fetchSubjectStats = async () => {
      if (availableSubjects.length === 0) return;

      try {
        const subjectNames = availableSubjects.map(s => s.name).join(',');
        const params = new URLSearchParams({ subjects: subjectNames });
        if (student?.student_id) {
          params.append('student_id', student.student_id);
        }

        const response = await authFetch(`/api/micro-learning/subject-stats?${params}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.stats) {
            const statsMap: Record<string, any> = {};
            data.stats.forEach((stat: any) => {
              statsMap[stat.subject] = stat;
            });
            setSubjectStats(statsMap);
          }
        }
      } catch (error) {
        console.error('Error fetching subject stats:', error);
      }
    };

    fetchSubjectStats();
  }, [availableSubjects, student?.student_id]);

  // Fetch study streak data
  useEffect(() => {
    const fetchStreak = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        const response = await fetch('/api/session/streak/current', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setStreakData(data);
        }
      } catch (error) {
        console.error('Error fetching streak:', error);
      }
    };

    fetchStreak();
  }, []);

  // Fetch diagnostic status
  useEffect(() => {
    const fetchDiagnosticStatus = async () => {
      try {
        const data = await diagnosticAPI.getStatus();
        setDiagnosticStatus(data.diagnostic_status || {});
      } catch (error) {
        console.error('Error fetching diagnostic status:', error);
      }
    };

    fetchDiagnosticStatus();
  }, []);

  // ESSLCE-prediction survey trigger. Two paths:
  //   1. Inbox deep-link — `?survey=open` query param → open immediately,
  //      strip the param so refresh doesn't re-fire.
  //   2. Auto-prompt — poll /state, open after 2.5s if should_prompt = true.
  // Cleanup cancels in-flight timeout if the student navigates away. The
  // modal lands at a natural rest point — never mid-flow.
  useEffect(() => {
    if (searchParams.get('survey') === 'open') {
      setShowSurvey(true);
      const next = new URLSearchParams(searchParams);
      next.delete('survey');
      setSearchParams(next, { replace: true });
      return;
    }
    let cancelled = false;
    const open = setTimeout(async () => {
      if (cancelled) return;
      try {
        const res = await authFetch('/api/gamification/prediction/state');
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.should_prompt) {
          setShowSurvey(true);
        }
      } catch {
        // Non-critical — survey re-checks on next Dashboard visit.
      }
    }, 2500);
    return () => {
      cancelled = true;
      clearTimeout(open);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubjectClick = (subjectName: string) => {
    track('dashboard.subject.clicked', { subject: subjectName });
    navigate('/micro-learning', {
      state: { defaultSubject: subjectName }
    });
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  return (
    <div className="dashboard">
      {/* The first-visit tutorial, the app-open feedback modal and the ESSLCE
          prediction survey were all disabled for the UAT launch. Each was
          defensible alone; together they meant a student's first screen could
          be three interruptions deep before they saw a question, and the one
          interruption that now has to land is the UAT announcement. Nothing is
          deleted — re-render any of them to bring it back.
      {showQuickStart && (
        <QuickStart
          studentName={student?.first_name}
          stream={student?.stream}
          onComplete={finishQuickStart}
          onSkip={finishQuickStart}
        />
      )}
      */}

      {/* Signal Engine app-open feedback modal — disabled, see above.
      <FeedbackAutoModal /> */}

      {/* UAT, at the top of the dashboard. For a student who does not have it:
          a modal once, then a card. For a student who DOES: a permanent way
          back into the package — this dashboard is the ESLCE one, and a UAT
          student who lands here through a notification or a bookmark
          previously had no route to the exam they are actually sitting.
          Placed AFTER QuickStart so a first-time student is never shown two
          interruptions stacked on their first screen. */}
      <UatPrompt />
      <EauPrompt />

      {/* The dark-mode announcement bar lived here. It was meant to run for a
          few days and had long outlived that, so it was taking permanent space
          above the header for news nobody needs any more. The component is
          still in the tree if it is ever wanted again. */}

      {/* Minimal Header */}
      <header className="compact-header minimal">
        <div className="header-content">
          <div className="brand-logo" onClick={() => navigate('/dashboard')}>
            <Logo height={32} withAmharic />
          </div>

          {/* The same switcher a package room has, in the same place.
              This dashboard IS a package — the free ESLCE one — and the only
              reason it did not say so was that the control lived inside
              PackageBar, which the dashboard does not use. Without it, moving
              from ESLCE to an exam you hold meant finding a promo card, and
              moving between two packages meant leaving to here first. */}
          <PackageSwitcher current="esslce" currentName="ESSLCE Prep" />

          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
            <InboxBell />

          {/* Account Menu */}
          <div className="account-menu-container" ref={accountMenuRef}>
            <button
              className="account-btn"
              onClick={() => {
                if (!showAccountMenu) track('dashboard.account_menu.opened', {});
                setShowAccountMenu(!showAccountMenu);
              }}
            >
              <Avatar
                url={student?.avatar_url}
                name={student?.first_name}
                size={32}
                className="account-avatar"
              />
              <svg className="chevron" width="16" height="16" viewBox="0 0 16 16">
                <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
            </button>

            {showAccountMenu && (
              <div className="account-dropdown">
                <div className="account-info">
                  <div className="account-name">{student?.first_name} {student?.last_name}</div>
                  <div className="account-details">
                    <span>Grade {student?.grade_level || '12'}</span>
                    <span className="separator">•</span>
                    <span>{student?.stream || 'Stream not set'}</span>
                  </div>
                </div>
                <hr className="dropdown-divider" />

                {/* Subscription Status in Dropdown */}
                <div className="dropdown-subscription">
                  <div className="subscription-status-mini">
                    {/* Subscription is disabled during the free-trial
                        launch window — every student is on the trial,
                        and the PaymentFlow page renders an empty form
                        on mobile. Always show the Trial badge; hide
                        the Upgrade entry point entirely. */}
                    <span className="status-badge active">✓ Active</span>
                    <span className="plan-name">Trial</span>
                  </div>
                </div>
                <hr className="dropdown-divider" />

                <button className="dropdown-item" onClick={() => { track('dashboard.profile.clicked', {}); navigate('/profile'); }}>
                  <svg width="16" height="16" viewBox="0 0 16 16">
                    <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 100-6 3 3 0 000 6z" fill="currentColor"/>
                  </svg>
                  Profile
                </button>
                {/* Hidden 2026-05-30 while we rework the messaging — flip to true to restore */}
                {false && <HelpShapeLomiItem />}
                <button className="dropdown-item" onClick={() => { track('dashboard.about.clicked', {}); navigate('/about'); }}>
                  <svg width="16" height="16" viewBox="0 0 16 16">
                    <path d="M8 15A7 7 0 108 1a7 7 0 000 14zm0-12a1 1 0 110 2 1 1 0 010-2zm-1 4h2v5H7V7z" fill="currentColor"/>
                  </svg>
                  About Lomi
                </button>
                <hr className="dropdown-divider" />
                <ThemeMenuToggle onToggle={(next) => track('dashboard.theme.changed', { theme: next })} />
                {/* Quick Start Tutorial entry — hidden 2026-05-03 because the
                    target route (/diagnostic-onboarding) currently lands users on
                    a Knowledge Check assessment screen rather than the intended
                    onboarding tour. Re-enable once the destination is rebuilt. */}
                <hr className="dropdown-divider" />
                <button className="dropdown-item logout-item" onClick={() => { track('dashboard.logout.clicked', {}); logout(); }}>
                  <svg width="16" height="16" viewBox="0 0 16 16">
                    <path d="M10 12l4-4-4-4m4 4H3" stroke="currentColor" strokeWidth="2" fill="none"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Greeting Section - in content area */}
        {/* Greeting hero removed for mobile density — the avatar + first
            name still live in the header, and the drill strip is the new
            top-of-fold action. Saves ~80px on iPhone SE-class screens. */}

        {/* Stream not set → we can't target the right subjects (rotation falls
            back to the union of all). One-tap set so questions match the exam. */}
        {!student?.stream && (
          <div
            style={{
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 12,
              padding: '12px 14px',
              marginBottom: 10,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: '#92400e', marginBottom: 8 }}>
              📚 Set your stream so we show the right subjects for your exam
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                data-testid="set-stream-natural"
                onClick={() => setStream('natural')}
                disabled={settingStream}
                style={{
                  flex: 1,
                  minWidth: 130,
                  padding: '9px 12px',
                  background: '#fff',
                  border: '1px solid #d97706',
                  borderRadius: 9,
                  color: '#b45309',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: settingStream ? 'default' : 'pointer',
                }}
              >
                🌿 Natural Science
              </button>
              <button
                data-testid="set-stream-social"
                onClick={() => setStream('social')}
                disabled={settingStream}
                style={{
                  flex: 1,
                  minWidth: 130,
                  padding: '9px 12px',
                  background: '#fff',
                  border: '1px solid #d97706',
                  borderRadius: 9,
                  color: '#b45309',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: settingStream ? 'default' : 'pointer',
                }}
              >
                🌍 Social Science
              </button>
            </div>
            <div style={{ fontSize: 11.5, color: '#a16207', marginTop: 6 }}>
              You can change this anytime in your profile.
            </div>
          </div>
        )}

        {/* Daily top-of-fold action strip. Behind REACT_APP_DAILY_FOCUS, the
            rotating-subject Daily Focus card replaces the Aptitude drill strip;
            otherwise the live drill stays. Both self-hide if state can't load. */}
        {process.env.REACT_APP_DAILY_FOCUS === 'true' ? (
          <DailyFocusCard />
        ) : (
          <AptitudeDrillCard />
        )}
        {/* Spaced review — secondary to the daily set (which already blends in
            reviews); a slim on-demand bar below it. Self-hides when nothing due. */}
        <MasterYourMisses />

        {/* Lomi Live banner/popup removed from the dashboard after the event
            (Jun 2026). The feature stays reachable from the Profile page. */}

        {/* Student stories — read-first social-proof strip occupying the slot the
            Lomi Live banner used. Surfaces a real published quote + a path to the
            /testimonials feed. Self-hides when nothing is published; dismissable
            per-session so it motivates without nagging during cram week. */}
        <TestimonialsPromo source="dashboard" />

        {/* Share your Lomi story — prominent, dismissible hero. Pull, not push
            (no outbound DMs). Self-hides once the student opts in (hideIfAlreadyDone)
            or taps "Not now" (persisted in localStorage). The permanent entry
            lives on /profile. */}
        {!storyInviteDismissed && (
          <StoryInviteCard
            source="dashboard"
            dismissible
            hideIfAlreadyDone
            requireEligible
            defaultName={student?.first_name || ''}
            onDismiss={() => {
              localStorage.setItem('lomi_story_invite_dismissed', '1');
              setStoryInviteDismissed(true);
            }}
          />
        )}

        {/* CoachCard hidden for launch — resume UX already handled by Lessons/Past Exams tabs.
           Re-enable once ESLCE topic frequency awareness is built (see BACKLOG.md). */}
        {/* {availableSubjects.length > 0 && (() => {
          const lastSubject = localStorage.getItem('lomi_last_subject');
          const coachSubject = (lastSubject && availableSubjects.some(s => s.name === lastSubject))
            ? lastSubject
            : availableSubjects[0].name;
          return (
          <CoachCard
            subject={coachSubject}
            location={localStorage.getItem('lomi_last_tab') === 'past_exams' ? 'past_exams' : 'dashboard'}
            onAction={(action: CoachAction) => {
              const subjectName = coachSubject;
              if (!subjectName) return;
              switch (action.type) {
                case 'continue_module':
                  navigate('/micro-learning', {
                    state: { defaultSubject: subjectName, autoStartModule: action.payload }
                  });
                  break;
                case 'practice_topic':
                  navigate('/micro-learning', {
                    state: { defaultSubject: subjectName, defaultTab: 'past_exams', defaultTopic: action.payload }
                  });
                  break;
                case 'start_past_exams':
                  navigate('/micro-learning', {
                    state: { defaultSubject: subjectName, defaultTab: 'past_exams' }
                  });
                  break;
                default: {
                  // Restore last visited tab (lessons or past exams)
                  const lastTab = localStorage.getItem('lomi_last_tab');
                  navigate('/micro-learning', {
                    state: { defaultSubject: subjectName, ...(lastTab === 'past_exams' ? { defaultTab: 'past_exams' } : {}) }
                  });
                  break;
                }
              }
            }}
            onStudyReport={() => setShowStudyReport(true)}
          />
          );
        })()} */}

        {/* ESSLCE prediction survey ("what will you score?") — disabled, see
            above. It asks about a DIFFERENT exam from the one we are launching,
            which is the worst possible thing to interrupt a UAT student with.
        <PredictionSurveyModal
          open={showSurvey}
          onClose={() => setShowSurvey(false)}
          stream={(student as any)?.stream || undefined}
        />
        */}

        {/* Study Report Modal */}
        <StudyReport
          isOpen={showStudyReport}
          onClose={() => setShowStudyReport(false)}
          onNavigate={(subject, topic) => {
            if (topic) {
              navigate('/micro-learning', {
                state: { defaultSubject: subject, defaultTab: 'past_exams', defaultTopic: topic }
              });
            } else {
              navigate('/micro-learning', {
                state: { defaultSubject: subject }
              });
            }
          }}
        />

        {/* Single rounded card with three tappable partitions
            (rank · streak · invite). Mirrors the Aptitude card frame so
            the above-the-fold strip reads as one cohesive surface. The
            rank segment is suppressed only when the student is last in
            a tiny cohort — same demotivation guard as before. */}
        <section className="status-strip" aria-label="Your study stats">
          {rankData.rank !== null && rankData.total !== null &&
           !(rankData.rank === rankData.total && rankData.total < 10) && (
            <button
              type="button"
              className="status-segment status-segment-rank"
              onClick={() => {
                track('dashboard.leaderboard.clicked', { rank: rankData.rank, xp: rankData.xp });
                navigate('/leaderboard');
              }}
              aria-label={`Open leaderboard · rank ${rankData.rank}`}
            >
              <Avatar url={student?.avatar_url} name={student?.profile_name || student?.first_name} size={20} className="status-segment-avatar" />
              <span className="status-segment-value">
                {(() => {
                  // Compact XP: 22,470 → "22.4k", 540 → "540". Avatar +
                  // numeric chip reads unambiguously as score on mobile.
                  if (rankData.xp === null) return `#${rankData.rank}`;
                  const xp = rankData.xp;
                  const compact = xp >= 1000
                    ? (xp >= 10000 ? `${Math.round(xp / 1000)}k` : `${(xp / 1000).toFixed(1)}k`)
                    : xp.toLocaleString();
                  return rankData.total >= 10 ? `${compact} · #${rankData.rank}` : compact;
                })()}
              </span>
            </button>
          )}

          <div className="status-segment status-segment-streak" aria-label={`${streakData.streak_days} day streak`}>
            <span className="status-segment-icon" aria-hidden>{streakData.streak_days > 0 ? '🔥' : '📚'}</span>
            <span className="status-segment-value">
              <span className="status-segment-count">{streakData.streak_days}</span>
              <span className="status-segment-label"> day</span>
            </span>
          </div>

          <ShareLomi
            renderTrigger={(openModal) => (
              <button
                type="button"
                className="status-segment status-segment-invite"
                onClick={openModal}
                aria-label="Invite a friend to Lomi"
              >
                <span className="status-segment-icon" aria-hidden>🎁</span>
                <span className="status-segment-value">Invite</span>
                <span className="status-segment-chevron" aria-hidden>›</span>
              </button>
            )}
          />
        </section>

        {/* KC nudge banner removed from above-the-fold for mobile density.
            The per-subject "Take check" CTA still lives on subject cards
            where students decide which subject to study — that's the
            natural place to convert. The diagnostic-onboarding page is
            also reachable from the assessment results screen. */}

        {/* Subjects Section — heading dropped: the grid below is
            self-explanatory on the dashboard. Saves ~50px of chrome. */}
        <section className="subjects-section">
          <div className="subjects-grid">
            {loadingSubjects ? (
              <div className="loading-subjects">
                <p>Loading subjects...</p>
              </div>
            ) : availableSubjects.length > 0 ? (
              availableSubjects.map((subject) => {
                const stats = subjectStats[subject.name];
                const topicCount = stats?.topic_count || 0;
                const completionPct = stats?.completion_percentage || 0;
                const diag = diagnosticStatus[subject.name];

                return (
                  <div key={subject.name} className="subject-card-wrapper">
                    <button
                      className="subject-card"
                      style={{
                        '--subject-color': subject.color,
                      } as React.CSSProperties}
                      onClick={() => handleSubjectClick(subject.name)}
                    >
                      <div
                        className="subject-icon"
                        style={{ backgroundColor: `${subject.color}15` }}
                      >
                        {subject.icon}
                      </div>
                      <div className="subject-content">
                        {/* In dark mode, drop the per-subject color (some, e.g.
                            Math's purple, are too dark to read) and let the
                            .subject-content h3 token color apply. */}
                        <h3 style={isDark ? undefined : { color: subject.color }}>{subject.name}</h3>
                        <span className="subject-topic-count">{topicCount} topics</span>
                      </div>
                      <div className="subject-progress">
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${completionPct}%`,
                              backgroundColor: subject.color
                            }}
                          ></div>
                        </div>
                        <span className="progress-text">{completionPct > 0 ? `${Math.round(completionPct)}%` : 'Start'}</span>
                      </div>
                    </button>
                    {diag?.available && (
                      <div className="diagnostic-badge-row">
                        {!diag.taken ? (
                          <button
                            className="diagnostic-badge available"
                            onClick={(e) => {
                              e.stopPropagation();
                              track('dashboard.knowledge_check.clicked', {
                                subject: subject.name,
                                assessment_id: diag.assessment_id,
                                question_count: diag.question_count,
                                already_taken: false,
                              });
                              navigate('/take-assessment', {
                                state: {
                                  assessment: {
                                    id: diag.assessment_id,
                                    title: `Knowledge Check - ${subject.name}`,
                                    question_count: diag.question_count,
                                  },
                                  isDiagnostic: true,
                                  isInitial: true,
                                }
                              });
                            }}
                          >
                            Knowledge Check
                          </button>
                        ) : (
                          <button
                            className="diagnostic-badge refresh"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const result = await diagnosticAPI.start(subject.name);
                                navigate('/take-assessment', {
                                  state: {
                                    assessment: {
                                      id: result.assessment_id,
                                      title: `Knowledge Check - ${subject.name}`,
                                      question_count: result.question_count,
                                    },
                                    isDiagnostic: true,
                                    isInitial: false,
                                  }
                                });
                              } catch (err) {
                                console.error('Failed to start diagnostic:', err);
                              }
                            }}
                          >
                            Retake Knowledge Check
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="no-subjects">
                <p>No subjects available for your stream</p>
              </div>
            )}
          </div>
        </section>

        {/* Old streak section removed — streak is now compact above subjects */}
      </main>
    </div>
  );
};

export default Dashboard;
